
import { defineComponent, reactive, onBeforeMount, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { ApplicantHistoryListRequest } from '@/requests/message'
import { MessageApplicantHistory, MessageAddressDepartment, RequestHeaderDetail } from '@/api'
import RequestHeaderLoginWrapper from '@/components/auth-wrappers/RequestHeaderLoginWrapper.vue'
import InputGroup from '@/components/layout/InputGroup.vue'
import DateDisplay from '@/components/displays/DateDisplay.vue'
import ApplicantMessageHistoryBody from '@/components/displays/ApplicantMessageHistoryBody.vue'
import { displaySenderOrRecipientName } from '@/services/messageAddress'
import MessageSenderOrRecipientName from '@/components/displays/MessageSenderOrRecipientName.vue'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import Logger from '@/services/logger'
import { RequestTypes } from '@/constants/enums'

/**
 * 申請情報の取得
 */
const getRequestHeader = async (id: number): Promise<RequestHeaderDetail | null> => {
  const request = new RequestHeaderReadRequest(id)
  await request.exec()
  if (request.success) {
    const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
    return data
  } else {
    return null
  }
}

/**
 * 申請者用回答履歴の表示コンポーネント
 * 受信欄はel-tooltip内でv-forを回せないため、繋げてから表示している
 */
export default defineComponent({
  name: 'ApplicantMessageHistory',
  components: {
    RequestHeaderLoginWrapper,
    InputGroup,
    DateDisplay,
    MessageSenderOrRecipientName,
    ApplicantMessageHistoryBody,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const messages = reactive<MessageApplicantHistory[]>([])
    const tableHeight = ref(0)
    const adjustHeight = 160
    const minHeight = 80
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    const requestHeader = ref<RequestHeaderDetail | null>(null)

    // 受信者名の結合処理（重複は削除）
    const addressNameJoin = (addresses: MessageAddressDepartment[], delimiter: string): string => {
      const nameArray = addresses.map((address) =>
        displaySenderOrRecipientName({
          isApplicant: address.is_applicant,
          isApplicantHistory: true,
          departmentMailName: address.department?.department_mail_name,
        })
      )
      return [...new Set(nameArray)].join(delimiter)
    }

    onBeforeMount(async () => {
      // 申請基本を取得する
      if (requestHeaderId.value == null) {
        Logger.info(`URLの申請基本IDが不正です ${route.params.request_header_id}`)
        router.push({ name: 'NotFound' })
        return
      } else {
        requestHeader.value = await getRequestHeader(requestHeaderId.value)
        if (requestHeader.value?.request_type !== RequestTypes.PROXIMITY) {
          // 近接工事では無ければ404へ飛ばす
          router.push({ name: 'NotFound' })
          return
        }
      }
      // 回答履歴一覧を取得する
      const request = new ApplicantHistoryListRequest()
      await request.exec()
      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<MessageApplicantHistory[]>
        messages.push(...data)
      }
      // テーブルの高さ調整
      const calcHeight = window.innerHeight - adjustHeight
      tableHeight.value = calcHeight > minHeight ? calcHeight : minHeight
    })

    return {
      route,
      messages,
      tableHeight,
      addressNameJoin,
      displaySenderOrRecipientName,
    }
  },
})
